import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL, getAuthHeaders } from '../services/api';

interface Event {
  event_id: string;
  event_type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  source: string;
  source_type: 'agent' | 'cloud' | 'console' | 'system';
  user_id: string;
  agent_id: string | null;
  instance_id: string | null;
  title: string;
  message: string;
  metadata: string | null;
  created_at: number;
}

interface EventStats {
  period: string;
  start_time: number;
  end_time: number;
  total_events: number;
  by_type: { event_type: string; count: number }[];
  by_severity: { severity: string; count: number }[];
  recent_critical: Event[];
}

const severityColors = {
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  error: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
};

const severityDotColors = {
  info: 'bg-blue-500',
  warning: 'bg-yellow-500',
  error: 'bg-orange-500',
  critical: 'bg-red-500',
};

const eventTypeLabels: Record<string, string> = {
  security_threat: 'Security Threat',
  security_blocked: 'Security Blocked',
  security_pam_detected: 'Access Detected',
  security_pam_challenge: 'Access Challenge',
  security_pam_verified: 'Access Verified',
  security_pam_denied: 'Access Denied',
  security_pam_blocked: 'Access Blocked',
  security_process_pam_blocked: 'Process Access Blocked',
  security_process_pam_allowed: 'Process Access Allowed',
  security_process_pam_challenge: 'Process Access Challenge',
  security_process_pam_verified: 'Process Access Verified',
  security_process_pam_denied: 'Process Access Denied',
  lifecycle_start: 'Lifecycle Start',
  lifecycle_stop: 'Lifecycle Stop',
  lifecycle_restart: 'Lifecycle Restart',
  lifecycle_status: 'Lifecycle Status',
  lifecycle_install: 'Lifecycle Install',
  lifecycle_discover: 'Lifecycle Discover',
  policy_updated: 'Policy Updated',
  policy_synced: 'Policy Synced',
  config_changed: 'Config Changed',
  agent_registered: 'Agent Registered',
  agent_update: 'Agent Update',
  agent_restart: 'Agent Restart',
  auth_success: 'Auth Success',
  auth_failure: 'Auth Failure',
  auth_logout: 'Auth Logout',
  activity: 'AI Interaction',
  tool_execution: 'Tool Execution',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

export default function Events() {
  const { firebaseUser } = useAuth();
  const { currentTeam } = useTeam();
  const [events, setEvents] = useState<Event[]>([]);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Debounce searchQuery → debouncedSearch (400ms)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setOffset(0);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchQuery]);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!firebaseUser) return;

    try {
      console.log('[Events] Firebase user UID:', firebaseUser.uid);
      const token = await firebaseUser.getIdToken();
      const params = new URLSearchParams();

      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (severityFilter) params.append('severity', severityFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      console.log('[Events] Fetching with params:', params.toString());
      const response = await fetch(
        `${API_URL}/api/v1/events?${params.toString()}`,
        {
          headers: getAuthHeaders(token),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      console.log('[Events] Fetched events:', data.events?.length, 'events, total:', data.total);
      console.log('[Events] First event:', data.events?.[0]);
      setEvents(data.events || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, currentTeam, eventTypeFilter, severityFilter, debouncedSearch, limit, offset]);

  const fetchStats = useCallback(async () => {
    if (!firebaseUser) return;

    try {
      const token = await firebaseUser.getIdToken();
      const response = await fetch(
        `${API_URL}/api/v1/events/stats?period=day`,
        {
          headers: getAuthHeaders(token),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [firebaseUser, currentTeam]);

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [fetchEvents, fetchStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchEvents();
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchEvents, fetchStats]);

  const handleRefresh = () => {
    setLoading(true);
    setStatsLoading(true);
    fetchEvents();
    fetchStats();
  };

  const handleClearFilters = () => {
    setEventTypeFilter('');
    setSeverityFilter('');
    setSearchQuery('');
    setOffset(0);
  };

  const filteredEvents = searchQuery
    ? events.filter(
        (event) =>
          event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.message.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : events;

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatRelativeTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Layout title="Events" subtitle="Security events and activity across your fleet">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-slate-300 dark:border-slate-600"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 shadow-lg transition-opacity"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      {statsLoading ? (
        <div className="mb-6 p-6 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
          <p className="text-slate-500 dark:text-slate-400">Loading statistics...</p>
        </div>
      ) : stats ? (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Events */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Total Events (24h)</h3>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total_events}</p>
          </div>

          {/* By Severity */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">By Severity</h3>
            <div className="space-y-2">
              {stats.by_severity.map((item) => (
                <div key={item.severity} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${severityDotColors[item.severity as keyof typeof severityDotColors]}`}></div>
                    <span className="text-sm capitalize text-slate-700 dark:text-slate-300">{item.severity}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Event Types */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Top Event Types</h3>
            <div className="space-y-2">
              {stats.by_type.slice(0, 5).map((item) => (
                <div key={item.event_type} className="flex items-center justify-between">
                  <span className="text-sm truncate text-slate-700 dark:text-slate-300">{eventTypeLabels[item.event_type] || item.event_type}</span>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="mb-6 p-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Event Type
            </label>
            <select
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value);
                setOffset(0);
              }}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="">All Types</option>
              <option value="security_threat">Security Threat</option>
              <option value="security_blocked">Security Blocked</option>
              <option value="security_pam_detected">Access Detected</option>
              <option value="security_pam_challenge">Access Challenge</option>
              <option value="security_pam_verified">Access Verified</option>
              <option value="security_pam_denied">Access Denied</option>
              <option value="security_pam_blocked">Access Blocked</option>
              <option value="security_process_pam_blocked">Process Access Blocked</option>
              <option value="security_process_pam_allowed">Process Access Allowed</option>
              <option value="security_process_pam_challenge">Process Access Challenge</option>
              <option value="security_process_pam_verified">Process Access Verified</option>
              <option value="security_process_pam_denied">Process Access Denied</option>
              <option value="lifecycle_start">Lifecycle Start</option>
              <option value="lifecycle_stop">Lifecycle Stop</option>
              <option value="lifecycle_restart">Lifecycle Restart</option>
              <option value="lifecycle_status">Lifecycle Status</option>
              <option value="policy_updated">Policy Updated</option>
              <option value="config_changed">Config Changed</option>
              <option value="auth_logout">Auth Logout</option>
              <option value="activity">AI Interaction</option>
              <option value="tool_execution">Tool Execution</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value);
                setOffset(0);
              }}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search title or message..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleClearFilters}
              className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            Events ({total} total, showing {filteredEvents.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 dark:text-slate-400">Loading events...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="p-6 text-center text-slate-500 dark:text-slate-400">No events found</div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {filteredEvents.map((event) => {
              const metadata = event.metadata ? JSON.parse(event.metadata) : {};
              return (
                <div
                  key={event.event_id}
                  onClick={() => setSelectedEvent(event)}
                  className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${severityColors[event.severity]}`}>
                          {event.severity.toUpperCase()}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {eventTypeLabels[event.event_type] || event.event_type}
                        </span>
                        {event.instance_id && (
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            {metadata.instance_name ? `${metadata.instance_name} (${event.instance_id.slice(0, 8)})` : event.instance_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                        {event.title}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-2">
                        {event.message}
                      </p>

                      {/* Metadata badges */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {metadata.user_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded border border-indigo-200 dark:border-indigo-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {metadata.user_name}
                          </span>
                        )}
                        {metadata.channel && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            {metadata.channel}
                          </span>
                        )}
                        {(metadata.hostname || metadata.machine_name) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded border border-purple-200 dark:border-purple-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                            {metadata.hostname || metadata.machine_name}
                          </span>
                        )}
                        {metadata.machine_os && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded border border-green-200 dark:border-green-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {metadata.machine_os}
                          </span>
                        )}
                        {metadata.source_ip && metadata.source_ip !== 'unknown' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded border border-cyan-200 dark:border-cyan-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            {metadata.source_ip}
                          </span>
                        )}
                        {metadata.browser && metadata.browser !== 'Unknown' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            {metadata.browser}
                          </span>
                        )}
                        {metadata.threats && Array.isArray(metadata.threats) && metadata.threats.length > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded border border-red-200 dark:border-red-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            {metadata.threats.length} threat{metadata.threats.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {formatRelativeTime(event.created_at)}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">
                        {formatTimestamp(event.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{selectedEvent.title}</h2>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${severityColors[selectedEvent.severity]}`}>
                    {selectedEvent.severity.toUpperCase()}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {eventTypeLabels[selectedEvent.event_type] || selectedEvent.event_type}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Message</h3>
                <p className="text-sm text-slate-900 dark:text-white">{selectedEvent.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Event ID</h3>
                  <p className="text-sm text-slate-900 dark:text-white font-mono">{selectedEvent.event_id}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Timestamp</h3>
                  <p className="text-sm text-slate-900 dark:text-white">{formatTimestamp(selectedEvent.created_at)}</p>
                </div>
                {selectedEvent.instance_id && (() => {
                  const meta = selectedEvent.metadata ? JSON.parse(selectedEvent.metadata) : {};
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Workspace</h3>
                      <p className="text-sm text-slate-900 dark:text-white">
                        {meta.instance_name ? `${meta.instance_name} (${selectedEvent.instance_id.slice(0, 8)})` : selectedEvent.instance_id}
                      </p>
                    </div>
                  );
                })()}
                {selectedEvent.agent_id && (() => {
                  const meta = selectedEvent.metadata ? JSON.parse(selectedEvent.metadata) : {};
                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Device</h3>
                      <p className="text-sm text-slate-900 dark:text-white">
                        {(meta.hostname || meta.machine_name) ? `${meta.hostname || meta.machine_name} (${selectedEvent.agent_id.slice(0, 8)})` : selectedEvent.agent_id}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {selectedEvent.metadata && (() => {
                const metadata = JSON.parse(selectedEvent.metadata);
                return (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Additional Details</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {metadata.user_name && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">User</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.user_name}</p>
                          {metadata.user_email && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">{metadata.user_email}</p>
                          )}
                        </div>
                      )}
                      {metadata.channel && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Channel</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.channel}</p>
                        </div>
                      )}
                      {(metadata.hostname || metadata.machine_name) && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Machine</h4>
                          <p className="text-sm text-slate-900 dark:text-white font-mono">{metadata.hostname || metadata.machine_name}</p>
                        </div>
                      )}
                      {metadata.machine_os && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Operating System</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.machine_os}</p>
                        </div>
                      )}
                      {metadata.instance_name && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Workspace</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.instance_name}</p>
                        </div>
                      )}
                      {metadata.source_ip && metadata.source_ip !== 'unknown' && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Source IP</h4>
                          <p className="text-sm text-slate-900 dark:text-white font-mono">{metadata.source_ip}</p>
                        </div>
                      )}
                      {metadata.browser && metadata.browser !== 'Unknown' && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Browser</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.browser}</p>
                        </div>
                      )}
                      {metadata.session_id && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Session ID</h4>
                          <p className="text-sm text-slate-900 dark:text-white font-mono">{metadata.session_id}</p>
                        </div>
                      )}
                      {metadata.message_len && (
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Message Length</h4>
                          <p className="text-sm text-slate-900 dark:text-white">{metadata.message_len} chars</p>
                        </div>
                      )}
                    </div>
                    {metadata.threats && Array.isArray(metadata.threats) && (
                      <div className="mb-4">
                        <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Detected Threats</h4>
                        <div className="space-y-2">
                          {metadata.threats.map((threat: any, idx: number) => (
                            <div key={idx} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
                              <div className="flex items-start gap-2 mb-1">
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700">
                                  {threat.severity?.toUpperCase() || 'UNKNOWN'}
                                </span>
                                <span className="text-sm font-medium text-red-900 dark:text-red-100">{threat.name}</span>
                              </div>
                              <p className="text-xs text-red-800 dark:text-red-200 mb-1">{threat.description}</p>
                              {threat.match && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-red-700 dark:text-red-300">Match:</span>
                                  <p className="text-xs text-red-600 dark:text-red-400 font-mono bg-red-100 dark:bg-red-900/30 p-1 rounded mt-1">
                                    {threat.match}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300">
                        View Raw Metadata
                      </summary>
                      <pre className="mt-2 bg-slate-50 dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700 overflow-x-auto text-slate-900 dark:text-white">
                        {JSON.stringify(metadata, null, 2)}
                      </pre>
                    </details>
                  </div>
                );
              })()}
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setSelectedEvent(null)}
                className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}
